<template>
  <div
    :class="{
      'b-custom-control-sm': size === 'sm',
      'b-custom-control-lg': size === 'lg',
    }"
    class="standard-checkbox custom-control custom-checkbox"
    @click="onRootClick"
  >
    <PVCheckbox
      v-model="model"
      :value="value"
      :input-id="id"
      :binary="binary"
      :indeterminate="indeterminate"
      :disabled="disabled"
      :pt="passThroughProps"
      unstyled
    />
    <label
      :for="id"
      :class="{
        'custom-control-input-checked': isChecked,
        'custom-control-input-indeterminate': indeterminate,
      }"
      class="custom-control-label"
      @click.prevent
    >
      {{ label }}
    </label>
  </div>
</template>
<script lang="ts">
export default {
  name: 'StandardCheckbox',
  compatConfig: { MODE: 3 },
};
</script>
<script setup lang="ts" generic="T">
import { PassThrough } from '@primevue/core';
import type { CheckboxPassThroughOptions } from 'primevue/checkbox';
import { computed } from 'vue';

const model = defineModel<boolean | T[]>({ required: true });

const props = withDefaults(
  defineProps<{
    id: string;
    label: string;
    value: T;
    binary?: boolean;
    indeterminate?: boolean;
    disabled?: boolean;
    size?: 'sm' | 'md' | 'lg';
  }>(),
  {
    binary: false,
    indeterminate: false,
    disabled: false,
    size: 'md',
  },
);

/** Mirrors Bootstrap's :checked for label styling; [] is truthy so don't use the model alone. */
const isChecked = computed((): boolean => {
  if (props.binary || typeof model.value === 'boolean') {
    return model.value === true;
  }

  return Array.isArray(model.value) && model.value.some((value) => value === props.value);
});

/**
 * Build PassThrough props for PrimeVue unstyled mode.
 * Applies Bootstrap button classes to match BootstrapVue styling.
 *
 * Details: https://primevue.org/passthrough/
 */
const passThroughProps = computed((): PassThrough<CheckboxPassThroughOptions> => {
  return {
    input: {
      class: ['custom-control-input'],
      style: { pointerEvents: 'none' },
    },
    // we handle styling through Bootstrap's classes, so hide the PrimeVue icon
    icon: {
      class: 'd-none',
    },
  };
});

const onRootClick = () => {
  if (props.disabled) return;

  if (props.binary || typeof model.value === 'boolean') {
    model.value = !(model.value as boolean);
    return;
  }

  if (!Array.isArray(model.value)) return;

  const selected = model.value;

  model.value = selected.includes(props.value)
    ? selected.filter((value) => value !== props.value)
    : [...selected, props.value];
};
</script>
